/**
 * Pure helpers for rendering a {@link VocabTerm} according to a vocabulary's
 * {@link VocabularyDisplayDto}. Kept free of React so they're trivially testable and
 * shared by the picker rows and the info panel.
 */
import type { VocabTerm, VocabularyDisplayDto } from '@/api/vocabularies';

/** Core (typed) term fields that a display `key` may reference directly. */
const CORE_LIST_FIELDS: Record<string, (t: VocabTerm) => readonly string[] | null | undefined> = {
  synonyms: (t) => t.synonyms,
  parents: (t) => t.parents,
  // Accept either casing — the live BE serialises camelCase but legacy
  // fixtures and older display configs reference snake_case.
  alt_ids: (t) => t.alt_ids ?? t.altIds,
  altIds: (t) => t.altIds ?? t.alt_ids,
  term_category: (t) => t.term_category ?? t.termCategory,
  termCategory: (t) => t.termCategory ?? t.term_category,
};
const CORE_SCALAR_FIELDS: Record<string, (t: VocabTerm) => string | null | undefined> = {
  id: (t) => t.id,
  name: (t) => t.name,
  def: (t) => t.def,
};

/**
 * Resolve a display `key` to its values: a core term field first, then the
 * `properties` map (suffix-stripped keys). Always returns an array (possibly empty).
 */
export function resolveFieldValues(term: VocabTerm, key: string): readonly string[] {
  const scalar = CORE_SCALAR_FIELDS[key];
  if (scalar !== undefined) {
    const v = scalar(term);
    return v != null && v !== '' ? [v] : [];
  }
  const list = CORE_LIST_FIELDS[key];
  if (list !== undefined) {
    return (list(term) ?? []).filter((v) => v !== '');
  }
  const prop = term.properties?.[key];
  return prop !== undefined ? prop.filter((v) => v !== '') : [];
}

/** The term's primary display title: `display.titleField` if it resolves, else name, else id. */
export function termTitle(term: VocabTerm, display?: VocabularyDisplayDto | null): string {
  const field = display?.titleField;
  if (field != null && field !== '') {
    const values = resolveFieldValues(term, field);
    if (values.length > 0) return values.join(', ');
  }
  return term.name != null && term.name !== '' ? term.name : term.id;
}

/** Optional secondary line under the title (e.g. HGNC gene name). `null` when absent. */
export function termSubtitle(term: VocabTerm, display?: VocabularyDisplayDto | null): string | null {
  const field = display?.subtitleField;
  if (field == null || field === '') return null;
  const values = resolveFieldValues(term, field);
  return values.length > 0 ? values.join(', ') : null;
}

/** Local part of a prefixed id (after the first `:`), e.g. `OMIM:617247` -> `617247`. */
export function localId(id: string): string {
  const i = id.indexOf(':');
  return i >= 0 ? id.slice(i + 1) : id;
}

/**
 * Fill a link template. Supported tokens: `{id}` (full id), `{localId}` (after `:`),
 * and `{v}` (an explicit per-value substitution). Returns `null` for an empty template.
 */
export function applyLinkTemplate(
  template: string | null | undefined,
  args: { readonly id?: string; readonly value?: string },
): string | null {
  if (template == null || template === '') return null;
  let out = template;
  if (args.id !== undefined) {
    out = out.replaceAll('{id}', encodeURIComponent(args.id)).replaceAll('{localId}', encodeURIComponent(localId(args.id)));
  }
  if (args.value !== undefined) {
    out = out.replaceAll('{v}', encodeURIComponent(args.value));
  }
  return out;
}
